import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import Papa from "papaparse";
import { useTranslation } from "react-i18next";
import { getCustomListCSV, uploadModifiedCustomList } from "src/services/AudienceListAPIs";
import type { CSVData } from "../../EngagementInterfaces";
import {
  buildRowFromValues,
  detectListColumns,
  type AddRowValues,
  type ListRow,
} from "./audienceListRows";

const MODIFIED_FILE_NAME = "modified.csv";
const CSV_MIME_TYPE = "text/csv";

export const useAudienceListRows = (listId: number | null) => {
  const { t } = useTranslation();
  const nextRowId = useRef(0);
  const [rows, setRows] = useState<ListRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const toListRows = useCallback(
    (data: CSVData[]) => data.map(row => ({ id: nextRowId.current++, data: row })),
    [],
  );

  useEffect(() => {
    if (listId === null) {
      return;
    }
    let cancelled = false;
    setRows([]);
    setHeaders([]);
    setDirty(false);
    setLoading(true);

    getCustomListCSV(listId)
      .then(blob => blob.text())
      .then(text => {
        if (cancelled) {
          return;
        }
        const { data, meta } = Papa.parse<CSVData>(text, { header: true, skipEmptyLines: true });
        setHeaders(meta.fields ?? Object.keys(data[0] ?? {}));
        setRows(toListRows(data));
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t("audienceOverview.listModal.loadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [listId, reloadToken, t, toListRows]);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  const columns = useMemo(() => detectListColumns(headers), [headers]);

  const addRow = useCallback(
    (values: AddRowValues) => {
      setRows(prev => [
        ...prev,
        { id: nextRowId.current++, data: buildRowFromValues(headers, columns, values) },
      ]);
      setDirty(true);
    },
    [headers, columns],
  );

  const deleteRow = useCallback((rowId: number) => {
    setRows(prev => prev.filter(row => row.id !== rowId));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    if (listId === null) {
      return false;
    }
    setSaving(true);
    try {
      const csv = Papa.unparse(rows.map(row => row.data), { columns: headers });
      const file = new File([csv], MODIFIED_FILE_NAME, { type: CSV_MIME_TYPE });
      await uploadModifiedCustomList(listId, file);
      setDirty(false);
      message.success(t("audienceOverview.listModal.saveSuccess"));
      return true;
    } catch {
      message.error(t("audienceOverview.listModal.saveFailed"));
      return false;
    } finally {
      setSaving(false);
    }
  }, [listId, rows, headers, t]);

  return { rows, columns, loading, saving, dirty, addRow, deleteRow, save, reload };
};
